import React from "react";
import { LoadingSpinner } from "../../../components/LoadingSpinner";

export type OfferImportKind = "gaeb" | "ugl" | "pdf";

export type OfferImportSource =
  | { type: "lead_document"; documentId: string }
  | { type: "email_attachment"; emailId: string; attachmentId: string };

type Candidate = {
  source: OfferImportSource;
  filename: string;
  size: number | null;
  origin: "project" | "email";
  originLabel: string;
  date: string | null;
};

function formatSize(bytes: number | null): string {
  if (bytes == null || !Number.isFinite(bytes)) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
}

function formatDate(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("de-DE");
}

function sourceKey(s: OfferImportSource): string {
  return s.type === "lead_document" ? `doc:${s.documentId}` : `mail:${s.emailId}:${s.attachmentId}`;
}

/**
 * Passende Dateien aus Projektdokumenten und Projekt-Mails – ein Klick importiert direkt,
 * statt Anhang herunterzuladen und wieder hochzuladen. Ohne Treffer ein kurzer Hinweis.
 */
export const OfferImportSourcePicker: React.FC<{
  offeringId: string;
  kind: OfferImportKind;
  disabled?: boolean;
  /** Gerade importierte Quelle (zeigt Spinner an der Zeile). */
  busySource?: OfferImportSource | null;
  onPick: (source: OfferImportSource, filename: string) => void;
}> = ({ offeringId, kind, disabled, busySource, onPick }) => {
  const [items, setItems] = React.useState<Candidate[] | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    setItems(null);
    fetch(`/api/gaeb/offerings/${encodeURIComponent(offeringId)}/import-candidates?kind=${kind}`, {
      credentials: "include",
    })
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((data: { items?: Candidate[] }) => {
        if (!cancelled) setItems(Array.isArray(data.items) ? data.items : []);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, [offeringId, kind]);

  if (items == null) {
    return (
      <div className="flex items-center gap-2 text-xs text-app-text-subtle">
        <LoadingSpinner size="sm" /> Dateien aus Projekt und E-Mails werden gesucht…
      </div>
    );
  }

  const busyKey = busySource ? sourceKey(busySource) : null;

  return (
    <div className="space-y-1.5">
      <p className="text-xs font-semibold text-app-text-subtle">Aus Projekt / E-Mail</p>
      {items.length === 0 ? (
        <p className="text-xs text-app-text-subtle">Keine passende Datei im Projekt oder in den Projekt-Mails gefunden.</p>
      ) : (
      <div className="max-h-56 overflow-y-auto space-y-0.5">
        {items.map((it) => {
          const key = sourceKey(it.source);
          const meta = [it.originLabel, formatDate(it.date), formatSize(it.size)].filter(Boolean).join(" · ");
          return (
            <button
              key={key}
              type="button"
              disabled={disabled}
              onClick={() => onPick(it.source, it.filename)}
              className="flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left hover:bg-app-hover disabled:opacity-60 disabled:pointer-events-none"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-app-text">{it.filename}</span>
                <span className="block truncate text-xs text-app-text-subtle">{meta}</span>
              </span>
              {busyKey === key ? <LoadingSpinner size="sm" /> : null}
            </button>
          );
        })}
      </div>
      )}
    </div>
  );
};
